/**
 * Ops simulation T09 — offline bills whose till tax rate differed from the
 * product master. Normal sync stores the master's rate; a bill an owner
 * accepted as sold keeps the till's. Either way a manager reviews it here.
 */
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageContainer, ErrorBoundary, Pagination, Skeleton, EmptyState } from '@bbpos/ui';
import { taxMismatchService, type TaxMismatch } from '@/services/tax-mismatch.service';
import { GLASS, cn } from '@/constants/design-tokens';
import { storeTimezone } from '@/lib/store-clock';
import { useAuthStore } from '@/stores/use-auth-store';

const PAGE_SIZE = 50;

const SOURCE_LABELS: Record<TaxMismatch['source'], string> = {
  SYNC: 'Stored at master rate',
  ACCEPTED_AS_SOLD: 'Accepted as sold',
};

const SOURCE_TONES: Record<TaxMismatch['source'], string> = {
  SYNC: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  ACCEPTED_AS_SOLD: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

/** On the bill's store clock (T28), not a fixed IST one. */
function formatDateTime(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
}

function TaxMismatchesContent() {
  const profile = useAuthStore((s) => s.profile);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? '1');

  const { data, isLoading, error } = useQuery({
    queryKey: ['bills', 'tax-mismatches', page],
    queryFn: () => taxMismatchService.list({ page, limit: PAGE_SIZE }),
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  return (
    <>
      <div className="mb-6 rounded-xl bg-gradient-to-r from-warning-500 to-danger-600 p-6 text-white shadow-lg animate-fade-in">
        <h2 className="text-2xl font-bold text-white">Tax mismatches</h2>
        <p className="mt-1 text-warning-50">
          Offline bills where the till charged a different tax rate from the product. Check the product's tax and the bill.
        </p>
        <p className="mt-3 inline-flex rounded-full bg-white/20 px-3 py-1 text-sm font-semibold tabular-nums">
          {total.toLocaleString('en-IN')} flagged
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} />
          ))}
        </div>
      ) : error ? (
        <div className={cn(GLASS, 'p-6 text-center text-sm text-danger-600 dark:text-danger-400')}>
          Could not load the tax mismatches. Please try again.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No tax mismatches"
          description="Every offline bill used the product's own tax rate."
          variant="page"
        />
      ) : (
        <div className={cn(GLASS, 'overflow-x-auto shadow-md')}>
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-warning-50 to-danger-50 text-left text-xs font-semibold uppercase text-neutral-600 dark:from-warning-900/20 dark:to-danger-900/10 dark:text-neutral-300">
              <tr>
                <th className="px-4 py-3">Bill</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3 text-right">Till rate</th>
                <th className="px-4 py-3 text-right">Product rate</th>
                <th className="px-4 py-3 text-right">On the bill</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Store</th>
                <th className="px-4 py-3">Cashier</th>
                <th className="px-4 py-3">Synced</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {rows.map((m) => (
                <tr
                  key={m.id}
                  data-testid={`tax-mismatch-${m.id}`}
                  className="transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 animate-slide-up dark:even:bg-neutral-800/40 dark:hover:bg-brand-900/20"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/bills/${m.bill_id}`}
                      className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                      {m.bill_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-neutral-800 dark:text-neutral-100">{m.product_name ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-danger-600 dark:text-danger-400">
                    {m.charged_gst_rate}%
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-700 dark:text-neutral-200">
                    {m.master_gst_rate}%
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-neutral-700 dark:text-neutral-200">
                    {m.stored_gst_rate}%
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', SOURCE_TONES[m.source])}>
                      {SOURCE_LABELS[m.source]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-200">{m.location_name ?? '—'}</td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-200">{m.cashier_name ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums text-neutral-500 dark:text-neutral-400">
                    {formatDateTime(m.created_at, storeTimezone(profile, m.location_id))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages ?? 1}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setSearchParams({ page: String(p) })}
          />
        </div>
      )}
    </>
  );
}

export function TaxMismatchesPage() {
  return (
    <PageContainer
      title="Tax Mismatches"
      subtitle="Offline bills taxed differently from the product"
      breadcrumbs={[{ label: 'Sync Health', href: '/sync-health' }, { label: 'Tax mismatches' }]}
    >
      <ErrorBoundary>
        <TaxMismatchesContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
